import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell } from "lucide-react";
import { api } from "../lib/api";

interface Notification {
  id: number; kind: string; title: string; body: string;
  link: string | null; readAt: string | null; createdAt: string;
}

const kindColor: Record<string, string> = {
  urgent_note: "bg-red-100 text-red-700",
  incident_reported: "bg-amber-50 text-amber-700",
  shift_assigned: "bg-teal-50 text-teal-800",
  timesheet_submitted: "bg-sky-50 text-sky-700",
};

export default function NotificationBell() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<Notification[]>([]);
  const [unread, setUnread] = useState(0);
  const ref = useRef<HTMLDivElement>(null);

  async function load() {
    try {
      const data = await api<{ items: Notification[]; unread: number }>("/v1/notifications");
      setItems(data.items);
      setUnread(data.unread);
    } catch { /* signed out */ }
  }
  useEffect(() => {
    load();
    const iv = setInterval(load, 30_000);
    return () => clearInterval(iv);
  }, []);
  useEffect(() => {
    const close = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  async function openItem(n: Notification) {
    await api(`/v1/notifications/${n.id}/read`, { method: "POST", body: "{}" });
    setOpen(false);
    load();
    if (n.link) navigate(n.link);
  }
  async function readAll() {
    await api("/v1/notifications/read-all", { method: "POST", body: "{}" });
    load();
  }

  return (
    <div ref={ref} className="relative">
      <button onClick={() => setOpen((v) => !v)} aria-label="Notifications"
        className="relative flex h-9 w-9 items-center justify-center rounded-lg text-mut hover:bg-hover hover:text-body transition-colors">
        <Bell className="h-[19px] w-[19px]" strokeWidth={1.8} />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-semibold text-white">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 top-11 z-50 w-96 card p-2 max-h-[70vh] overflow-y-auto">
          <div className="flex items-center justify-between px-3 py-2">
            <p className="text-sm font-medium text-ink">Notifications</p>
            {unread > 0 && (
              <button onClick={readAll} className="text-xs text-teal-700 hover:underline">Mark all read</button>
            )}
          </div>
          {items.length === 0 && <p className="px-3 py-6 text-sm text-faint text-center">Nothing yet.</p>}
          {items.map((n) => (
            <button key={n.id} onClick={() => openItem(n)}
              className={`w-full text-left rounded-xl px-3 py-2.5 transition-colors hover:bg-hover ${n.readAt ? "opacity-60" : ""}`}>
              <div className="flex items-center gap-2">
                <span className={`chip ${kindColor[n.kind] ?? "bg-slate-100 text-body"}`}>{n.kind.replace("_", " ")}</span>
                {!n.readAt && <span className="h-1.5 w-1.5 rounded-full bg-teal-500" />}
              </div>
              <p className="mt-1 text-sm font-medium text-ink">{n.title}</p>
              <p className="text-xs text-mut truncate">{n.body}</p>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
